import React, { useCallback, useEffect, useMemo, useState } from "react";

import moment from "moment-timezone";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useHistory, useLocation } from "react-router-dom";

import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Snackbar from "@material-ui/core/Snackbar";
import IconButton from "@material-ui/core/IconButton";
import Link from "@material-ui/core/Link";
import { makeStyles } from "@material-ui/core/styles";
import MuiAlert from "@material-ui/lab/Alert";
import { AgGridReact } from "ag-grid-react";

import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import LinkIcon from "@material-ui/icons/Link";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import CloseIcon from "@material-ui/icons/Close";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { fetchPayoutById } from "../../../redux/features/sales/payoutsSlice";
import { wcOrderAdminUrl } from "./wcLinks";

const useStyles = makeStyles((theme) => ({
  backButton: {
    marginBottom: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  infoGrid: {
    marginTop: theme.spacing(1),
  },
  labelText: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: theme.spacing(0.5),
  },
  valueText: {
    fontWeight: 600,
    fontSize: "1rem",
  },
  transferValue: {
    fontWeight: 700,
    fontSize: "1.25rem",
    color: "#19a86d",
  },
  commissionValue: {
    fontWeight: 700,
    fontSize: "1.25rem",
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  textWithIcon: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300,
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
}));

const fmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const money = (value) =>
  value !== null && value !== undefined ? fmt.format(Number(value)) : "";

const moneyFormatter = (params) => money(params.value);

const dateOnly = (value) =>
  value ? moment(value, "YYYY-MM-DD").format("ll") : "";

const placedAt = (value) =>
  value ? moment(value).tz("America/Chicago").format("lll") : "";

// Sums in cents so a long payout doesn't drift by a penny.
const sumMoney = (rows, field) =>
  rows.reduce((total, row) => total + Math.round(Number(row[field]) * 100), 0) /
  100;

const InfoField = ({ label, value, className }) => {
  const classes = useStyles();
  return (
    <div>
      <Typography className={classes.labelText}>{label}</Typography>
      <Typography className={className || classes.valueText}>
        {value || value === 0 ? value : "—"}
      </Typography>
    </div>
  );
};

const WcPayoutView = () => {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const { mode } = useSelector((state) => state.ui);
  const { currentPayout, isLoading, error } = useSelector(
    (state) => state.payouts
  );

  useEffect(() => {
    dispatch(fetchPayoutById(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (location.state?.showSuccess) {
      setSnackbar({
        open: true,
        message:
          location.state.snackbarContent || "Payout created successfully!",
      });
      history.replace(location.pathname, {});
    }
  }, [history, location]);

  const payout = currentPayout?.payout;
  const orders = useMemo(() => currentPayout?.orders || [], [currentPayout]);

  // The rate actually withheld, as recorded on the orders when they were
  // paid - not whatever the setting says today.
  const commissionPct = useMemo(() => {
    if (orders.length) return Number(orders[0].commission_pct);
    const metadata =
      typeof payout?.metadata === "string"
        ? JSON.parse(payout.metadata)
        : payout?.metadata;
    return metadata?.commission_pct ?? null;
  }, [orders, payout]);

  const totalStripeFees = useMemo(
    () => sumMoney(orders, "stripe_fee"),
    [orders]
  );

  const handleExportPdf = useCallback(() => {
    if (!payout) return;
    const doc = new jsPDF({ orientation: "landscape" });

    doc.setFontSize(16);
    doc.text(`EnjoySPI WooCommerce Payout #${payout.payment_id}`, 14, 18);

    doc.setFontSize(10);
    const col1x = 14;
    const col2x = 110;
    let y = 28;

    const line = (label, value, x) => {
      doc.setTextColor(120);
      doc.text(label, x, y);
      doc.setTextColor(0);
      doc.text(String(value ?? "—"), x + 40, y);
    };

    line("Date:", payout.created ? placedAt(payout.created) : "—", col1x);
    line("Commission Rate:", `${commissionPct ?? "—"}%`, col2x);
    y += 7;
    line("Orders:", orders.length, col1x);
    line("Payment Type:", "WOOCOMMERCE UPGRADES", col2x);
    y += 7;
    line("Status:", payout.payment_status?.toUpperCase() ?? "—", col1x);
    if (payout.stripe_transfer_id) {
      line("Stripe Transfer ID:", payout.stripe_transfer_id, col2x);
    }
    y += 7;
    line("Property:", payout.property_name || "—", col1x);
    line("Bank Account:", payout.stripe_account_name ?? "—", col2x);
    y += 7;
    if (payout.stripe_account_id) {
      line("Stripe Account ID:", payout.stripe_account_id, col1x);
      doc.setTextColor(0, 100, 200);
      doc.textWithLink("View on Stripe", col2x, y, {
        url: `https://dashboard.stripe.com/connect/accounts/${payout.stripe_account_id}`,
      });
      doc.setTextColor(0);
      y += 7;
    }
    if (payout.comments) {
      line("Comments:", payout.comments, col1x);
      y += 7;
    }
    y += 3;
    doc.setDrawColor(200);
    doc.line(14, y, 283, y);
    y += 7;

    const summaryItems = [
      ["Orders Total", money(payout.total_orders_amount)],
      ["Stripe Fees", money(totalStripeFees)],
      ["EnjoySPI Commission", money(payout.commission_amount)],
      ["Transfer Amount", money(payout.total_paid)],
    ];
    summaryItems.forEach(([label, value], i) => {
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(label.toUpperCase(), 14 + i * 68, y);
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text(value, 14 + i * 68, y + 6);
    });
    y += 16;

    autoTable(doc, {
      startY: y,
      head: [
        [
          "Order ID",
          "Customer",
          "Reservation Name",
          "Reservation #",
          "Date Placed",
          "Check In",
          "Check Out",
          "Product",
          "Qty",
          "Order Total",
          "Stripe Fee",
          "Commission",
          "Transfer Amount",
        ],
      ],
      body: orders.map((o) => [
        o.id,
        o.customer_name ?? "",
        o.reservation_name ?? "",
        o.reservation_number ?? "",
        placedAt(o.date_order_placed),
        dateOnly(o.check_in),
        dateOnly(o.check_out),
        o.product_name ?? "",
        o.quantity ?? "",
        money(o.total),
        money(o.stripe_fee),
        money(o.commission),
        money(o.transfer_amount),
      ]),
      foot: [
        [
          "Total",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          money(payout.total_orders_amount),
          money(totalStripeFees),
          money(payout.commission_amount),
          money(payout.total_paid),
        ],
      ],
      styles: { fontSize: 7 },
      headStyles: { fillColor: [40, 40, 40] },
      footStyles: { fillColor: [235, 235, 235], textColor: 0 },
    });

    doc.save(
      `enjoyspi_woocommerce-payout-${payout.payment_id}_${moment(
        payout.created
      ).format("YYYY-MM-DD")}.pdf`
    );
  }, [payout, orders, commissionPct, totalStripeFees]);

  const OrderIdCellRenderer = useCallback((params) => {
    const orderId = params.value;
    if (!orderId) return null;
    return (
      <a
        href={wcOrderAdminUrl(orderId)}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "inherit", fontWeight: "bold" }}
      >
        {orderId}
      </a>
    );
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Order ID",
        field: "id",
        filter: "agNumberColumnFilter",
        width: 110,
        pinned: "left",
        cellRendererFramework: OrderIdCellRenderer,
      },
      {
        headerName: "Customer",
        field: "customer_name",
        filter: "agTextColumnFilter",
        minWidth: 160,
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Reservation Name",
        field: "reservation_name",
        filter: "agTextColumnFilter",
        minWidth: 160,
      },
      {
        headerName: "Reservation #",
        field: "reservation_number",
        filter: "agTextColumnFilter",
        minWidth: 140,
      },
      {
        headerName: "Date Placed",
        field: "date_order_placed",
        minWidth: 160,
        valueFormatter: (params) => placedAt(params.value),
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Check In",
        field: "check_in",
        minWidth: 120,
        valueFormatter: (params) => dateOnly(params.value),
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Check Out",
        field: "check_out",
        minWidth: 120,
        valueFormatter: (params) => dateOnly(params.value),
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Product",
        field: "product_name",
        filter: "agTextColumnFilter",
        minWidth: 220,
      },
      {
        headerName: "Qty",
        field: "quantity",
        type: "rightAligned",
        width: 80,
        minWidth: 80,
      },
      {
        headerName: "Order Total",
        field: "total",
        type: "rightAligned",
        valueFormatter: moneyFormatter,
      },
      {
        headerName: "Stripe Fee",
        field: "stripe_fee",
        type: "rightAligned",
        valueFormatter: moneyFormatter,
      },
      {
        headerName: "Commission",
        field: "commission",
        type: "rightAligned",
        valueFormatter: moneyFormatter,
        cellStyle: { fontWeight: "bold" },
      },
      {
        headerName: "Transfer Amount",
        field: "transfer_amount",
        type: "rightAligned",
        pinned: "right",
        width: 160,
        valueFormatter: moneyFormatter,
        cellStyle: { color: "#19a86d", fontWeight: "bold" },
      },
    ],
    [OrderIdCellRenderer]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 110,
    }),
    []
  );

  const backButton = (
    <Button
      className={classes.backButton}
      startIcon={<ArrowBackIcon />}
      onClick={() => history.push("/sales/woocommerce-payouts")}
    >
      Back to WooCommerce Payouts
    </Button>
  );

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  if (error || !payout) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {backButton}
          <Typography color="error">
            {error?.errorMessage || "Payout not found."}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  if (payout.payment_type !== "wc_upgrade") {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {backButton}
          <Typography>
            Payout #{payout.payment_id} is not a WooCommerce payout.{" "}
            <Link
              component="button"
              onClick={() =>
                history.push(`/sales/payouts/${payout.payment_id}`)
              }
            >
              Open it in Payouts
            </Link>
          </Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {backButton}
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPdf}
          >
            Export Report PDF
          </Button>
        </Grid>

        {/* Payout Header */}
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Typography variant="h6">
                  WooCommerce Payout #{payout.payment_id}
                </Typography>
              </Grid>
              <Grid item>
                <Chip
                  label={payout.payment_status?.toUpperCase()}
                  size="small"
                  style={{
                    backgroundColor:
                      payout.payment_status === "complete"
                        ? "#19a86dbb"
                        : "#dfad1dbb",
                    color: "white",
                    fontWeight: "bold",
                  }}
                />
              </Grid>
              {!!payout.reversed && (
                <Grid item>
                  <Chip
                    label="REVERSED"
                    size="small"
                    style={{
                      backgroundColor: "#de4141bb",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  />
                </Grid>
              )}
            </Grid>

            <Divider className={classes.divider} />

            <Grid container spacing={3} className={classes.infoGrid}>
              <Grid item xs={6} sm={3}>
                <InfoField
                  label="Date"
                  value={payout.created ? placedAt(payout.created) : "—"}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <InfoField
                  label="Commission Rate"
                  value={commissionPct !== null ? `${commissionPct}%` : "—"}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <InfoField label="Orders" value={orders.length} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <InfoField label="Payment Type" value="WOOCOMMERCE UPGRADES" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField label="Property" value={payout.property_name} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField
                  label="Bank Account"
                  value={payout.stripe_account_name}
                />
                {payout.stripe_account_id && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ display: "block" }}
                  >
                    {payout.stripe_account_id}
                  </Typography>
                )}
                {payout.stripe_account_id && (
                  <Link
                    href={`https://dashboard.stripe.com/connect/accounts/${payout.stripe_account_id}`}
                    target="_blank"
                    rel="noopener"
                    className={classes.textWithIcon}
                    style={{ marginTop: 4 }}
                  >
                    <LinkIcon fontSize="small" />
                    View Account on Stripe
                  </Link>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField
                  label="Stripe Transfer ID"
                  value={payout.stripe_transfer_id}
                />
                {payout.stripe_transfer_id && (
                  <Link
                    href={`https://dashboard.stripe.com/connect/transfers/${payout.stripe_transfer_id}`}
                    target="_blank"
                    rel="noopener"
                    className={classes.textWithIcon}
                    style={{ marginTop: 4 }}
                  >
                    <LinkIcon fontSize="small" />
                    View Transfer on Stripe
                  </Link>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField label="Comments" value={payout.comments} />
              </Grid>
            </Grid>

            <Divider className={classes.divider} />

            {/* Financial Summary */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <InfoField
                  label="Orders Total"
                  value={money(payout.total_orders_amount)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <InfoField label="Stripe Fees" value={money(totalStripeFees)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography className={classes.labelText}>
                  Our Commission
                </Typography>
                <Typography className={classes.commissionValue}>
                  {money(payout.commission_amount)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography className={classes.labelText}>
                  Transfer Amount
                </Typography>
                <Typography className={classes.transferValue}>
                  {money(payout.total_paid)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Orders Table */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Orders in this Payout ({orders.length})
          </Typography>
          <div
            className={
              mode === "dark" ? "ag-theme-alpine-dark" : "ag-theme-alpine"
            }
            style={{
              height: Math.min(600, Math.max(200, orders.length * 42 + 60)),
              width: "100%",
            }}
          >
            <AgGridReact
              rowData={orders}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              enableCellTextSelection
              animateRows
            />
          </div>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity="success"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          action={
            <>
              <Button
                color="inherit"
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => {
                  handleExportPdf();
                  setSnackbar((s) => ({ ...s, open: false }));
                }}
                disabled={isLoading || !payout}
              >
                Download PDF
              </Button>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => setSnackbar((s) => ({ ...s, open: false }))}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default WcPayoutView;
